import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Space,
  Switch,
  Divider,
  Select,
  Card,
  notification,
  Tabs,
  Radio,
  Flex,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { get, toPairs, cloneDeep, omit } from 'lodash';
import { useSelector } from 'react-redux';
import { useSsps } from '../../hooks/useSsps';
import { WhiteOrBlackList } from '../../components';
import SellerInfo from '../../pages/authorized/ssps/SellerInfo';
import { authorizedDELETE } from '../../helpers/requester';
import { list } from '../../constants/endpoints';
import { WhiteAndBlackListName, WhiteAndBlackListType } from '../../apiGateway/whiteAndBlackLists';
import { useDisabledModules } from '../../hooks/useDisabledModules';

// Using Tabs in place of Collapse to keep sections visible without unmounting

const defaultDspFields = {
  partnerId: '',
  name: '',
  host: '',
  port: 80,
  path: '',
  useBanner: false,
  useVideo: false,
  callNurls: false,
  mobileWeb: false,
  inApp: false,
  desktop: false,
  instlOnly: 0,
  minTmax: 0,
  reqUserId: 0,
  useAdapter: 0,
  dailySpendLimit: 0,
  totalSpendLimit: 0,
  maxBidfloor: 0,
  onlyStoredApps: 0,
  onlyMatchedUsers: 0,
  mismatchIpFilter: 0,
  mismatchBundlesFilter: 0,
  lowSecondPrice: 0,
  adapterLearningPercent: 0,
  PMB: 0,
  PMW: 0,
  FB: 0,
  FW: 0,
  secure: false,
  margin: 0,
  limitQps: 0,
  maxSupplyChainNodes: 0,
  supplyChainCompleted: false,
  isActive: false,
  includeExtraMargin: false,
  extraMargin: 0,
  note: '',
  custom: null,
  useGzip: false,
  noImpBidding: false,
  dnsResolve: false,
  inBannerVideo: false,
  videoPlacementType: 2,
  optimizer: true,

  // disabled fields
  useNative: false,
  videoStartTracker: false,

  // removed fields
  isBlocked: false,
  usePop: false,
};

const getEndpointUrl = (dspFields) => {
  if (dspFields.endpointUrl) {
    return dspFields.endpointUrl;
  }

  if (dspFields.host && dspFields.port && dspFields.path) {
    return [
      dspFields.host.replace('https://', '').replace('http://', ''),
      dspFields.port === 80 ? '' : `:${dspFields.port}`,
      dspFields.path,
    ].join('');
  }

  return '';
};

export default function UpsertDspForm(props) {
  const { ssps } = useSsps();
  const [form] = Form.useForm();

  const [dspFields, setDspFields] = useState({});
  const [endpointUrlType, setEndpointUrlType] = useState(
    (get(props, 'dsp.host') || '').includes('internal') ? 'internal' : 'external'
  );
  const [activeTab, setActiveTab] = useState('general');

  const userInfo = useSelector((state) => state.login);
  const isAdmin = userInfo?.role === 'admin';
  const hasInBannerVideo = !useDisabledModules().includes('inBannerVideo');

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const dspFields = {
      // eslint-disable-next-line react/prop-types
      ...(props.dsp || defaultDspFields),
      // eslint-disable-next-line react/prop-types
      endpointUrl: getEndpointUrl(props.dsp || defaultDspFields),
      protocol: (get(props, 'dsp.host') || '').replace(/:\/\/.+/, '://') || 'http://',
      // eslint-disable-next-line react/prop-types
      authUsername: (props.dsp && get(JSON.parse(props.dsp.custom || '{}'), 'authorization.username')) || null,
      // eslint-disable-next-line react/prop-types
      authPassword: (props.dsp && get(JSON.parse(props.dsp.custom || '{}'), 'authorization.password')) || null,
      // eslint-disable-next-line react/prop-types
      headers: (props.dsp && get(JSON.parse(props.dsp.custom || '{}'), 'headers')) || [],
      // eslint-disable-next-line react/prop-types
      bidRequestFields: toPairs((props.dsp && get(JSON.parse(props.dsp.custom || '{}'), 'bidRequestFields')) || {}).map(
        ([key, value]) => ({ key, value })
      ),
    };

    setDspFields(dspFields);
    form.setFieldsValue(dspFields);
  }, [props]);

  const onFinish = (values) => {
    if (values.margin && values.extraMargin) {
      notification.error({
        title: 'Please, use margin OR revenue share',
        description: `margin: ${values.margin}, revenue share: ${values.extraMargin}`,
        duration: 0,
      });

      return;
    }

    const canChangeCustom = userInfo && ['admin', 'localAdmin'].includes(userInfo.role);

    const existingCustom =
      typeof dspFields.custom === 'string'
        ? (() => {
            try {
              return JSON.parse(dspFields.custom || '{}');
            } catch (error) {
              return {};
            }
          })()
        : dspFields.custom || {};

    const hasHeadersFromValues = values.headers !== undefined;
    const hasBidRequestFieldsFromValues = values.bidRequestFields !== undefined;

    const headersFromForm = hasHeadersFromValues ? values.headers : (dspFields.headers ?? existingCustom.headers ?? []);
    const bidRequestFieldsFromForm = hasBidRequestFieldsFromValues
      ? values.bidRequestFields
      : (dspFields.bidRequestFields ??
        Object.entries(existingCustom.bidRequestFields || {}).map(([key, value]) => ({ key, value })));

    const mergedHeaders = headersFromForm
      .filter((header) => header && (header.key || header.value))
      .map((header) => ({
        ...header,
      }));

    const mergedBidRequestFields = bidRequestFieldsFromForm.reduce((acc, cur) => {
      if (!cur || !cur.key) {
        return acc;
      }

      return {
        ...acc,
        [cur.key]: cur.value,
      };
    }, {});

    const mergedAuthorization = {
      username: values.authUsername ?? dspFields.authUsername ?? get(existingCustom, 'authorization.username', null),
      password: values.authPassword ?? dspFields.authPassword ?? get(existingCustom, 'authorization.password', null),
    };

    const customPayload = {
      ...existingCustom,
      authorization: mergedAuthorization,
      headers: hasHeadersFromValues
        ? mergedHeaders
        : mergedHeaders.length
          ? mergedHeaders
          : existingCustom.headers || [],
      bidRequestFields:
        hasBidRequestFieldsFromValues || Object.keys(mergedBidRequestFields).length
          ? mergedBidRequestFields
          : existingCustom.bidRequestFields || {},
    };

    // eslint-disable-next-line react/prop-types
    props.onSubmit(
      omit(
        cloneDeep({
          ...dspFields,
          ...values,
          endpointUrl: `${values.protocol || ''}${values.endpointUrl}`,
          custom: canChangeCustom
            ? JSON.stringify(customPayload)
            : // eslint-disable-next-line react/prop-types
              props?.dsp?.custom || '{}',
        }),
        [
          'authUsername',
          'authPassword',
          'headers',
          'bidRequestFields',
          'inventoryRequirement',
          'environmentRequirement',
        ]
      )
    );
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleEndpointTypeChange = (e) => {
    setEndpointUrlType(e.target.value);
  };

  const onDelete = (baseUrl) => async (e, data) => {
    try {
      await authorizedDELETE(baseUrl.replace(':id', data.id));
      notification.success({ title: 'Deleted' });
    } catch (error) {
      notification.error({ title: `Oops.. sth went wrong: ${error}` });
    }
  };

  const baseFormTabs = [
    { key: 'general', label: 'General' },
    { key: 'advanced', label: 'Advanced' },
    { key: 'userSync', label: 'User Sync' },
  ];

  const formTabsWithLists = dspFields.id
    ? [...baseFormTabs, { key: 'whiteLists', label: 'White Lists' }, { key: 'blackLists', label: 'Black Lists' }]
    : baseFormTabs;

  const headerTabs = dspFields.id
    ? [...formTabsWithLists, { key: 'sellerInfo', label: 'RTB Seller Info' }]
    : formTabsWithLists;

  useEffect(() => {
    if (activeTab === 'sellerInfo' && !dspFields.id) {
      setActiveTab('general');
    }
  }, [activeTab, dspFields.id]);

  const hideSubmitTabs = ['whiteLists', 'blackLists'];
  const shouldShowSubmitButton = !hideSubmitTabs.includes(activeTab);

  return (
    <div>
      <Tabs
        destroyOnHidden={false}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={headerTabs.map((tab) => ({ key: tab.key, label: tab.label }))}
      />

      {activeTab === 'sellerInfo' && dspFields.id ? (
        <SellerInfo dspId={dspFields.id} />
      ) : (
        <Form
          name="basic"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          form={form}
        >
          <Tabs
            destroyOnHidden={false}
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            renderTabBar={() => null}
            items={[
              {
                key: 'general',
                label: 'General',
                children: (
                  <>
                    <Card title="Overview" size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={12}>
                        <Col xs={24} sm={8}>
                          <Form.Item label="Active" valuePropName="checked" name="isActive" style={{ marginBottom: 8 }}>
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={16}>
                          <Form.Item label="Name" name="name" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                            <Input placeholder="Enter DSP name" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label="Endpoint Url" style={{ marginBottom: 0 }}>
                        <Flex>
                          <Radio.Group
                            style={{ display: 'flex', marginRight: 10 }}
                            value={endpointUrlType}
                            onChange={handleEndpointTypeChange}
                          >
                            <Radio.Button value="external">External</Radio.Button>
                            <Radio.Button value="internal">Internal</Radio.Button>
                          </Radio.Group>

                          <Form.Item name="endpointUrl" rules={[{ required: true }]} style={{ width: '100%' }}>
                            {endpointUrlType === 'internal' && (
                              <Select
                                placeholder="Select an SSP"
                                options={ssps.map((x) => ({
                                  label: `[${x.id}] ${x.name}`,
                                  value: `internal://${x.id}/`,
                                }))}
                                showSearch
                                filterOption={(input, option) =>
                                  ((option && option.label) || '').toLowerCase().includes(input.toLowerCase())
                                }
                              />
                            )}

                            {endpointUrlType === 'external' && (
                              <Input
                                placeholder="example.com:80/path?query=1"
                                addonBefore={
                                  <Form.Item noStyle name="protocol">
                                    <Select defaultValue="http://" name="protocol">
                                      <Select.Option value="http://">http://</Select.Option>
                                      <Select.Option value="https://">https://</Select.Option>
                                    </Select>
                                  </Form.Item>
                                }
                              />
                            )}
                          </Form.Item>
                        </Flex>
                      </Form.Item>
                    </Card>

                    <Card title="Traffic & Inventory" size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={[8, 0]}>
                        <Col xs={12} sm={8} md={6} lg={4}>
                          <Form.Item
                            name="useBanner"
                            label="Banner"
                            valuePropName="checked"
                            style={{ marginBottom: 8 }}
                          >
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                          <Form.Item name="useVideo" label="Video" valuePropName="checked" style={{ marginBottom: 8 }}>
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                          <Form.Item
                            name="useNative"
                            label="Native"
                            valuePropName="checked"
                            style={{ marginBottom: 8 }}
                          >
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                          <Form.Item
                            name="mobileWeb"
                            label="Mobile Web"
                            valuePropName="checked"
                            style={{ marginBottom: 8 }}
                          >
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                          <Form.Item name="inApp" label="In-App" valuePropName="checked" style={{ marginBottom: 8 }}>
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                          <Form.Item name="desktop" label="Desktop" valuePropName="checked" style={{ marginBottom: 8 }}>
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        name="inventoryRequirement"
                        dependencies={['useBanner', 'useVideo', 'useNative']}
                        rules={[
                          () => ({
                            validator() {
                              const hasInventoryType = ['useBanner', 'useVideo', 'useNative'].some((field) =>
                                Boolean(form.getFieldValue(field))
                              );
                              return hasInventoryType
                                ? Promise.resolve()
                                : Promise.reject(new Error('Select at least one: Banner, Video, or Native.'));
                            },
                          }),
                        ]}
                        validateTrigger="onBlur"
                        style={{ marginBottom: 0 }}
                      >
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item
                        name="environmentRequirement"
                        dependencies={['mobileWeb', 'inApp', 'desktop']}
                        rules={[
                          () => ({
                            validator() {
                              const hasEnvironment = ['mobileWeb', 'inApp', 'desktop'].some((field) =>
                                Boolean(form.getFieldValue(field))
                              );
                              return hasEnvironment
                                ? Promise.resolve()
                                : Promise.reject(new Error('Select at least one: Mobile Web, In-App, or Desktop.'));
                            },
                          }),
                        ]}
                        validateTrigger="onBlur"
                        style={{ marginBottom: 0 }}
                      >
                        <Input type="hidden" />
                      </Form.Item>
                    </Card>

                    <Card title="Delivery & Network" size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={12}>
                        <Col xs={24} sm={8}>
                          <Form.Item name="secure" label="Secure (https)" valuePropName="checked">
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="useGzip"
                            label="Gzip Requests"
                            tooltip="Send bid requests only in Gzip"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="noImpBidding"
                            label="No Imp Bidding"
                            tooltip="DSP will receive bid requests but never win"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="dnsResolve"
                            label="Resolve DNS"
                            tooltip="Can be enabled per request"
                            valuePropName="checked"
                          >
                            <Switch disabled />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="optimizer"
                            label="Optimizer"
                            tooltip="Managed setting"
                            valuePropName="checked"
                          >
                            <Switch disabled={!isAdmin} />
                          </Form.Item>
                        </Col>
                        {hasInBannerVideo && (
                          <Col xs={24} sm={8}>
                            <Form.Item name="inBannerVideo" label="Convert Banner to Video" valuePropName="checked">
                              <Switch />
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24} sm={8}>
                          <Form.Item
                            label="Video Placement Type"
                            name="videoPlacementType"
                            tooltip="OpenRTB Video Placement Types"
                          >
                            <Select
                              placeholder="Select type"
                              options={[
                                { value: 1, label: '1 - In-Stream' },
                                { value: 2, label: '2 - In-Banner' },
                                { value: 3, label: '3 - In-Article' },
                                { value: 4, label: '4 - In-Feed' },
                                { value: 5, label: '5 - Interstitial/Slider/Floating' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item name="videoStartTracker" label="Video Start Tracker" valuePropName="checked">
                        <Switch disabled />
                      </Form.Item>
                    </Card>

                    <Card title="Limits" size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Min tmax (ms)"
                            name="minTmax"
                            tooltip="Minimum auction timeout in milliseconds"
                          >
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label="QPS Limit" name="limitQps" tooltip="Maximum bid requests per second">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item label="Max Bidfloor" name="maxBidfloor">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label="Daily Spend Limit" name="dailySpendLimit">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item label="Total Spend Limit" name="totalSpendLimit">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>

                    <Card title="Supply Chain" size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Max Nodes"
                            name="maxSupplyChainNodes"
                            help={
                              'Limits source.ext.schain.nodes to the last N entries before sending. Set to 0 to keep the full chain.'
                            }
                          >
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="supplyChainCompleted"
                            label="Mark as Completed"
                            valuePropName="checked"
                            help={
                              'Applied to 100% of bid requests. When enabled, source.ext.schain.complete is set to 1 in DSP bid requests.'
                            }
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        label="Domains Blacklist"
                        name="supplyChainDomainsBlacklist"
                        help={
                          'Comma-separated domains of supply chain sellers to drop. Matching nodes are removed from source.ext.schain.nodes.'
                        }
                        normalize={(value) =>
                          typeof value === 'string'
                            ? value
                                .trim()
                                .replace(/\s{2,}/g, ' ')
                                .toLowerCase()
                            : value
                        }
                      >
                        <Input style={{ width: '100%' }} placeholder="example.com, example2.com" />
                      </Form.Item>
                    </Card>

                    <Form.Item label="Notes">
                      <Input.TextArea name="note" allowClear />
                    </Form.Item>

                    <Card title="Pricing" size="small" style={{ width: '100%', marginBottom: 20 }}>
                      <Form.Item
                        label="Margin Percent"
                        name="margin"
                        help="Example: DSP win 3.00 with 15% margin → SSP sees 2.55. Ignored if either side sets revenue share."
                      >
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item
                        label="Revenue Share Percent"
                        name="extraMargin"
                        help="Example: win price 4.00, bidfloor 3.00, 25% share → we keep 0.25. Turns off margin logic on both sides."
                      >
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>

                      <Card.Meta description="Priority: SSP revenue share → DSP revenue share → DSP margin. If both shares are 0, margin applies; otherwise we keep the original bidfloor." />
                    </Card>
                  </>
                ),
              },
              {
                key: 'advanced',
                label: 'Advanced',
                children: (
                  <>
                    <Card title="Custom Bid Request Fields" style={{ width: '100%', marginBottom: 20 }}>
                      <Form.List name="bidRequestFields">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space
                                key={key}
                                style={{
                                  display: 'flex',
                                  marginBottom: 8,
                                }}
                                align="baseline"
                              >
                                <Form.Item {...restField} name={[name, 'key']}>
                                  <Input placeholder="Key" />
                                </Form.Item>

                                <Form.Item {...restField} name={[name, 'value']}>
                                  <Input placeholder="Value" />
                                </Form.Item>

                                <MinusCircleOutlined onClick={() => remove(name)} />
                              </Space>
                            ))}
                            <Form.Item>
                              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                Add custom bid request field
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>

                      <Card.Meta
                        description="Custom Fields let you add or override values in the outgoing DSP bid
  request. Enter key/path pairs where the key is the field path; all changes apply before the
  request is sent. Example: imp[].tagid = 123"
                      />
                    </Card>

                    <Card title="Authorization" style={{ width: '100%', marginBottom: 20 }}>
                      <Form.Item label="Username" name="authUsername">
                        <Input />
                      </Form.Item>

                      <Form.Item label="Password" name="authPassword">
                        <Input />
                      </Form.Item>

                      <Card.Meta
                        description={
                          <>
                            <p>
                              Enter the username and password your DSP gave you. We automatically include them with each
                              bid request so the DSP can confirm it’s from your account.
                            </p>
                            <p>
                              Technical: Uses HTTP Basic Auth. Adds an Authorization header of{' '}
                              <code>Basic &lt;base64(username:password)&gt;</code> to every bid request.
                            </p>
                          </>
                        }
                      />
                    </Card>

                    <Card title="Headers" style={{ width: '100%', marginBottom: 20 }}>
                      <Form.List name="headers">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space
                                key={key}
                                style={{
                                  display: 'flex',
                                  marginBottom: 8,
                                }}
                                align="baseline"
                              >
                                <Form.Item {...restField} name={[name, 'key']}>
                                  <Input placeholder="Key" />
                                </Form.Item>

                                <Form.Item {...restField} name={[name, 'value']}>
                                  <Input placeholder="Value" />
                                </Form.Item>

                                <MinusCircleOutlined onClick={() => remove(name)} />
                              </Space>
                            ))}
                            <Form.Item>
                              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                Add header
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                      <Card.Meta
                        description={
                          <>
                            <p>Add any extra headers to include with each DSP bid request.</p>
                            <p>
                              Technical: Sends key/value pairs as HTTP request headers on every request (e.g.,{' '}
                              <code>Content-Type</code>, <code>X-Custom-Header</code>). If Basic Auth is configured
                              above, the Authorization header is added automatically.
                            </p>
                          </>
                        }
                      />
                    </Card>
                  </>
                ),
              },
              {
                key: 'userSync',
                label: 'User Sync',
                children: (
                  <>
                    <Form.Item name="enableUserSync" valuePropName="checked">
                      <Checkbox>Enable User Sync</Checkbox>
                    </Form.Item>

                    <Form.Item
                      label="User Sync Tag URL"
                      name="userSyncEndpointUrl"
                      help={
                        <p>
                          Use <code>{'X_USER_SYNC_ENDPOINT'}</code> as a placeholder for the URL-encoded address of our
                          user sync service.
                        </p>
                      }
                    >
                      <Input.TextArea placeholder="e.g., https://visitor-gen01.omnitagjs.com/visitor/bsync?uid=49aa1eb94c9034631f0a5ceb16651fbe&name=gen01&url=X_USER_SYNC_ENDPOINT" />
                    </Form.Item>

                    <Form.Item
                      label="User Sync Tag Type"
                      name="userSyncTagType"
                      help="Select the type of user sync tag to be used."
                    >
                      <Select style={{ width: '100%' }} placeholder="Choose a tag type">
                        <Select.Option value="iframe">iframe</Select.Option>
                        <Select.Option value="img">img</Select.Option>
                      </Select>
                    </Form.Item>
                  </>
                ),
              },
              ...(dspFields.id
                ? [
                    {
                      key: 'whiteLists',
                      label: 'White Lists',
                      children: (
                        <>
                          <WhiteOrBlackList
                            title="Add SSP to WL"
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="ssp_id"
                            parseValFunc={Number}
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            allowedValues={props.allowedWhiteListSspValues}
                            hiddenColumns={['id']}
                            additionalRequestParams={{
                              name: WhiteAndBlackListName.whiteListDsp,
                              type: WhiteAndBlackListType.White,
                              value: 'x',
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add sizes to WL"
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            helpTxt="format: 320x320, 180x240"
                            onDelete={onDelete(list.v2Delete)}
                            allowedValues={props.allowedWhiteListSizesValues}
                            hiddenColumns={['id']}
                            additionalRequestParams={{
                              name: WhiteAndBlackListName.whiteListDspSizes,
                              type: WhiteAndBlackListType.White,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add OS to WL"
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            allowedValues={props.allowedWhiteListOsValues}
                            hiddenColumns={['id']}
                            additionalRequestParams={{
                              name: WhiteAndBlackListName.whiteListDspOs,
                              type: WhiteAndBlackListType.White,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add Countries to WL"
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            allowedValues={props.allowedWhiteListCountryValues}
                            hiddenColumns={['id']}
                            additionalRequestParams={{
                              name: WhiteAndBlackListName.whiteListDspCountries,
                              type: WhiteAndBlackListType.White,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add Bundles to WL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="whiteListDspBundles"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.whiteListDspBundles,
                              type: WhiteAndBlackListType.White,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add Domains to WL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="whiteListDspDomains"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.whiteListDspDomains,
                              type: WhiteAndBlackListType.White,
                            }}
                          />

                          <WhiteOrBlackList
                            title="Add Publishers to WL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="whiteListDspPublishers"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.whiteListDspPublishers,
                              type: WhiteAndBlackListType.White,
                            }}
                          />
                        </>
                      ),
                    },
                  ]
                : []),
              ...(dspFields.id
                ? [
                    {
                      key: 'blackLists',
                      label: 'Black Lists',
                      children: (
                        <>
                          <WhiteOrBlackList
                            title="Add IPs to BL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="blackListDspIPs"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.blackListDspIPs,
                              type: WhiteAndBlackListType.Black,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add Bundles to BL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="blackListDspBundles"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.blackListDspBundles,
                              type: WhiteAndBlackListType.Black,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add Domains to BL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="blackListDspDomains"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.blackListDspDomains,
                              type: WhiteAndBlackListType.Black,
                            }}
                          />
                          <hr />

                          <WhiteOrBlackList
                            title="Add Publishers to BL"
                            urlTemplate={list.v2}
                            id={dspFields.id}
                            primaryKeyName="dsp_id"
                            keyName="value"
                            postUrl={list.v2}
                            onDelete={onDelete(list.v2Delete)}
                            listName="blackListDspPublishers"
                            additionalRequestParams={{
                              dsp_id: dspFields.id,
                              name: WhiteAndBlackListName.blackListDspPublishers,
                              type: WhiteAndBlackListType.Black,
                            }}
                          />
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          {shouldShowSubmitButton && (
            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                {dspFields.id ? 'Update' : 'Create'}
              </Button>
            </Form.Item>
          )}
        </Form>
      )}

      <Divider />
    </div>
  );
}

UpsertDspForm.propTypes = {
  dsp: PropTypes.object,
  userInfo: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  allowedWhiteListCountryValues: PropTypes.array,
  allowedWhiteListSspValues: PropTypes.array,
  allowedWhiteListSizesValues: PropTypes.array,
  allowedWhiteListOsValues: PropTypes.array,
};

UpsertDspForm.defaultProps = {
  dsp: undefined,
  userInfo: undefined,
  allowedWhiteListCountryValues: [],
  allowedWhiteListSspValues: [],
  allowedWhiteListSizesValues: [],
  allowedWhiteListOsValues: [],
};
